<template>
    <base-view :base-store="modelPricingSore" :title="$t('models.title')">
        <template #content>
            <crud-table
                class="ma-6"
                :after-create="[modelPricingSore.reload]"
                :base-store="modelPricingSore"
                :default-item="defaultItem"
                :table-headers="tableHeaders"
                :edit-title="$t('models.editTitle')"
                :create-btn-title="$t('models.createBtnTitle')"
                :create-title="$t('models.createTitle')"
                :delete-confirm-text="$t('remove')"
                :delete-abort-text="$t('cancel')"
                :delete-title="$t('models.deleteTitle')"
                suppress-delete-cancel-message
                suppress-delete-confirm-message
                suppress-edit-cancel-message
                suppress-edit-confirm-message
            >
                <template #deleteForm>
                    <p>{{ $t('models.deleteText') }}</p>
                </template>
                <template #editForm="{focusItem, outprops, onSave, onCancel}">

                    <v-row>
                        <application-picker
                            v-model="focusItem.app_id"
                            prepend-icon="mdi-application"
                            :hint="$t('models.app_id.hint')"
                            :label="$t('models.app_id.lbl')"
                        />
                        <text-field v-model="focusItem.name"
                                    prepend-icon="mdi-tag"
                                    :hint="$t('models.name.hint')"
                                    :label="$t('models.name.lbl')"/>
                        <text-field v-model="focusItem.config_type"
                                    type="select"
                                    item-title="name"
                                    item-value="value"
                                    prepend-icon="mdi-cog"
                                    :select-items="[
                                        {'name': 'LLM', 'value': 'llm'},
                                        {'name': 'Embedding', 'value': 'embedding'},
                                        {'name': 'VectorStore', 'value': 'vectorstore'},
                                        {'name': 'Document', 'value': 'document'},
                                    ]"
                                    :hint="$t('models.config_type.hint')"
                                    :label="$t('models.config_type.lbl')"/>
                    </v-row>
                    <v-row>
                        <text-field v-model="focusItem.version"
                                    prepend-icon="mdi-numeric-7-box"
                                    :hint="$t('models.version.hint')"
                                    :label="$t('models.version.lbl')"/>
                        <text-field v-model="focusItem.calc_model_name"
                                    prepend-icon="mdi-calculator"
                                    :hint="$t('models.calc_model_name.hint')"
                                    :label="$t('models.calc_model_name.lbl')"/>
                    </v-row>
                    <v-row v-if="focusItem.config_type === 'llm' || focusItem.config_type === 'embedding'">
                        <text-field v-model="focusItem.cloud_provider"
                                    type="select"
                                    item-title="name"
                                    item-value="value"
                                    prepend-icon="mdi-cloud"
                                    :select-items="cloudProviderOptions"
                                    :hint="$t('models.cloud_provider.hint')"
                                    :label="$t('models.cloud_provider.lbl')"
                                    @update:model-value="onCloudProviderChange(focusItem)"/>
                        <text-field v-model="focusItem.model_provider"
                                    type="select"
                                    item-title="name"
                                    item-value="value"
                                    prepend-icon="mdi-brain"
                                    :select-items="getModelProviderOptions(focusItem.cloud_provider)"
                                    :hint="$t('models.model_provider.hint')"
                                    :label="$t('models.model_provider.lbl')"/>
                    </v-row>
                    <v-row>
                        <text-field v-model="focusItem.api_url"
                                    prepend-icon="mdi-api"
                                    :hint="$t('models.api_url.hint')"
                                    :label="$t('models.api_url.lbl')"/>
                        <text-field v-model="focusItem.api_key"
                                    type="password"
                                    prepend-icon="mdi-key"
                                    password-icon
                                    :hint="$t('models.api_key.hint')"
                                    :label="$t('models.api_key.lbl')"/>
                    </v-row>

                    <!-- Allow-lists (LLM / Embedding only) -->
                    <template v-if="focusItem.config_type === 'llm' || focusItem.config_type === 'embedding'">
                        <v-divider class="my-4" />
                        <div class="text-subtitle-2 mb-2">{{ $t('models.allowLists.title') }}</div>
                        <v-row>
                            <v-col cols="6">
                                <v-combobox
                                    v-model="focusItem.supported_tools"
                                    :items="['get_current_date', 'search_web', 'http_request', 'wait', 'knowledge_base_search', 'code_execution', 'image_generation', 'list_artifacts']"
                                    :label="$t('models.supported_tools.lbl')"
                                    :hint="$t('models.supported_tools.hint')"
                                    prepend-icon="mdi-tools"
                                    variant="outlined"
                                    density="compact"
                                    multiple
                                    chips
                                    closable-chips
                                    clearable
                                    persistent-hint
                                />
                            </v-col>
                            <v-col cols="6">
                                <v-combobox
                                    v-model="focusItem.supported_mcp_servers"
                                    :items="['playwright', 'azdevops']"
                                    :label="$t('models.supported_mcp_servers.lbl')"
                                    :hint="$t('models.supported_mcp_servers.hint')"
                                    prepend-icon="mdi-server-network"
                                    variant="outlined"
                                    density="compact"
                                    multiple
                                    chips
                                    closable-chips
                                    clearable
                                    persistent-hint
                                />
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Capability flags (LLM only) -->
                    <template v-if="focusItem.config_type === 'llm'">
                        <v-divider class="my-4" />
                        <div class="text-subtitle-2 mb-2">{{ $t('models.capabilities.title') }}</div>
                        <v-row dense>
                            <v-col cols="6">
                                <v-switch v-model="focusItem.supports_responses" :label="$t('models.capabilities.responses')" color="primary" density="compact" hide-details />
                            </v-col>
                            <v-col cols="6">
                                <v-switch v-model="focusItem.supports_reasoning" :label="$t('models.capabilities.reasoning')" color="primary" density="compact" hide-details />
                            </v-col>
                            <v-col cols="6">
                                <v-switch v-model="focusItem.supports_thinking" :label="$t('models.capabilities.thinking')" color="primary" density="compact" hide-details />
                            </v-col>
                            <v-col cols="6">
                                <v-switch v-model="focusItem.supports_vision" :label="$t('models.capabilities.vision')" color="primary" density="compact" hide-details />
                            </v-col>
                            <v-col cols="6">
                                <v-switch v-model="focusItem.supports_caching" :label="$t('models.capabilities.caching')" color="primary" density="compact" hide-details />
                            </v-col>
                            <v-col cols="6">
                                <v-switch v-model="focusItem.supports_adaptive_thinking" :label="$t('models.capabilities.adaptiveThinking')" color="primary" density="compact" hide-details />
                            </v-col>
                        </v-row>
                    </template>

                    <action-btn :confirm-text="$t('save')"
                                :cancel-text="$t('cancel')"
                                @save="onSave"
                                @cancel="onCancel"/>
                </template>
                <template #app_id="{item}">
                    {{appStore.items?.find(i => i.id === item.app_id)?.name}}
                </template>
                <template #config_type="{item}">
                    <v-chip :color="item.config_type === 'llm' ? 'primary' : item.config_type === 'embedding' ? 'warning' : item.config_type === 'document' ? 'info' : 'secondary'" size="small">
                        {{ item.config_type === 'llm' ? 'LLM' : item.config_type === 'embedding' ? 'Embedding' : item.config_type === 'document' ? 'Document' : 'VectorStore' }}
                    </v-chip>
                </template>
            </crud-table>
        </template>
    </base-view>
</template>

<script lang="ts">
import {computed, defineComponent} from "vue"
import CrudTable from "@/components/generics/CrudTable/CrudTable.vue";
import BaseView from "@/views/BaseView.vue";
import ActionBtn from "@/components/generics/ActionBtn/ActionBtn.vue";
import TextField from "@/components/generics/TextField/TextField.vue";
import {useI18n} from "vue-i18n";
import {useModelPricingSore} from "@/stores/ModelPricingStore/ModelPricingStore";
import {IModelPricing, CloudProviderType, LLM_PROVIDER_COMBINATIONS} from "@/stores/ModelPricingStore/interfaces/IModelPricing";
import {useApplicationStore} from "@/stores/ApplicationStore/ApplicationStore";
import ApplicationPicker from "@/components/picker/ApplicationPicker.vue";


export default defineComponent({
    components: {
        ApplicationPicker,
        TextField,
        ActionBtn,
        BaseView,
        CrudTable,
    },
    setup() {
        const modelPricingSore = useModelPricingSore()
        const appStore = useApplicationStore()
        const {t} = useI18n();
        const defaultItem: Omit<IModelPricing, 'id'> = {
            app_id: 1,
            name: "",
            config_type: "llm",
            version: null,
            calc_model_name: null,
            api_key: "",
            api_url: "",
            cloud_provider: "azure",
            model_provider: "open_ai",
            supported_tools: null,
            supported_mcp_servers: null,
            supports_responses: true,
            supports_reasoning: true,
            supports_thinking: false,
            supports_vision: true,
            supports_caching: false,
            supports_adaptive_thinking: false,
        }

        const cloudProviderOptions = [
            { name: 'Azure', value: 'azure' },
            { name: 'Google', value: 'google' },
            { name: 'Ollama', value: 'ollama' },
            { name: 'OpenAI', value: 'openai' },
            { name: 'Perplexity', value: 'perplexity' },
        ]

        const getModelProviderOptions = (cloudProvider: CloudProviderType | null | undefined) => {
            if (!cloudProvider) return []
            const providers = LLM_PROVIDER_COMBINATIONS[cloudProvider] || []
            return providers.map(p => ({
                name: p === 'open_ai' ? 'OpenAI' : p === 'anthropic' ? 'Anthropic' : p.charAt(0).toUpperCase() + p.slice(1),
                value: p
            }))
        }

        const onCloudProviderChange = (item: IModelPricing) => {
            // Reset model_provider when cloud_provider changes
            const validProviders = LLM_PROVIDER_COMBINATIONS[item.cloud_provider as CloudProviderType] || []
            if (!item.model_provider || !validProviders.includes(item.model_provider)) {
                item.model_provider = validProviders[0] || null
            }
        }

        const tableHeaders = computed(() => [
            {title: t('models.table.app_id'), align: 'start', sortable: true, key: 'app_id'},
            {title: t('models.table.name'), align: 'start', sortable: true, key: 'name'},
            {title: t('models.table.config_type'), align: 'start', sortable: true, key: 'config_type'},
            {title: t('models.table.api_url'), align: 'start', sortable: true, key: 'api_url'},
            {title: t('models.table.actions'), align: 'end', sortable: false, key: 'Actions'},
        ])

        return {
            // composable
            modelPricingSore,
            // const
            defaultItem,
            cloudProviderOptions,
            // methods
            getModelProviderOptions,
            onCloudProviderChange,
            // computed
            tableHeaders,
            appStore
        }
    },
})
</script>
